import React from 'react'
import log from 'loglevel'
import { type ConfigProps } from 'components/Dashboard'
import { type Partner } from '@mzc-watchover/adminapp-appsync'
import {
  Datagrid,
  Empty,
  FunctionField,
  ListBase,
  ListToolbar,
  Loading,
  NotFound,
  Pagination,
  TextField,
  TopToolbar,
  useGetIdentity,
  useListContext,
  usePermissions,
  useTranslate
} from 'react-admin'
import { Button, Card, Typography } from '@mui/material'
import { isNil, keys, map } from 'lodash/fp'
import { Download, Upload } from '@mui/icons-material'
import { EditDataUploadDialog } from 'components/Upload'
import { EditDataDownloadDialog } from 'components/Download'
import { type Mapping } from '../../../../cdk/bapp/lib/lambda-api-v2/src/types'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

export const EditData: React.FC<
  ConfigProps & { partner: Partial<Partner> }
> = ({ config, partner }) => {
  const { isLoading: isGetIdentityLoading, data: identityData } =
    useGetIdentity()
  const { isLoading: isPermissionsLoading } = usePermissions()
  const { SpotList: configSpotList } = config
  const [upload, setUpload] = React.useState(false)
  const [download, setDownload] = React.useState(false)
  const { sortBy } = configSpotList
  const sort: { field: string; order: 'ASC' | 'DESC' } = {
    field: isNil(sortBy) ? 'name' : sortBy,
    order: 'ASC'
  }
  const translate = useTranslate()
  const resource = 'mappings'
  const handleUpload = (): void => {
    setUpload(true)
  }
  const handleDownload = (): void => {
    setDownload(true)
  }
  const ListActions: React.FC = () => {
    return (
      <TopToolbar>
        <Button
          onClick={handleUpload}
          startIcon={<Upload />}
          variant="contained">
          <Typography variant="h6">
            {translate('resources.editData.buttons.upload')}
          </Typography>
        </Button>
        <Button
          onClick={handleDownload}
          startIcon={<Download />}
          variant="contained">
          <Typography variant="h6">
            {translate('resources.editData.buttons.download')}
          </Typography>
        </Button>
      </TopToolbar>
    )
  }
  if (isGetIdentityLoading || isPermissionsLoading) return <Loading />
  if (isNil(identityData)) return <NotFound />
  const EditDataContent: React.FC = () => {
    const { total, isLoading } = useListContext()
    if (!isNil(isLoading) && isLoading) return null
    if (total === 0) return <Empty />
    return (
      <>
        <ListToolbar actions={<ListActions />} />
        <Card>
          <Datagrid id="spotListContent" bulkActionButtons={false}>
            {map((key: string) => {
              const { label, width } = configSpotList.fields[key]
              return key === 'tenant' ? (
                <FunctionField
                  sx={{ display: 'block', width }}
                  label={label ?? key}
                  render={(record: Mapping) => {
                    return (
                      <Typography variant="inherit">
                        {!isNil(record.tenant) && record.tenant ? '○' : ''}
                      </Typography>
                    )
                  }}
                  key={key}
                />
              ) : (
                <TextField
                  key={key}
                  source={key}
                  label={label ?? key}
                  sx={{ display: 'block', width }}
                />
              )
            })(keys(configSpotList.fields))}
          </Datagrid>
        </Card>
        <Pagination />
      </>
    )
  }
  return (
    <>
      <ListBase
        resource={resource}
        disableSyncWithLocation
        debounce={1000}
        filterDefaultValues={{ tenant: true }}
        sort={sort}>
        <EditDataContent />
      </ListBase>
      {!isNil(partner) && (
        <EditDataUploadDialog
          open={upload}
          setOpen={setUpload}
          partner={partner}
        />
      )}
      {!isNil(partner) && (
        <EditDataDownloadDialog
          open={download}
          setOpen={setDownload}
          partner={partner}
          config={config}
        />
      )}
    </>
  )
}
